<template>
    <div class="container-fluid" id="featuresContainer">
        <div id="selectedSummary" class="d-flex align-items-center">
            <p aria-live="polite">{{ selectedCount }} selected</p>
            <button id="clearFeatures" @click="clearSelections()">Clear all</button>
        </div>
        <div class="row" id="featuresWrapper">
            <div 
                v-for="(feature, index) in features"
                class="feature-wrap col-4 d-flex justify-content-center"
            >
                <button 
                    class="featureButton d-flex flex-column" 
                    :class="{selected: feature.pressed}"
                    @click="featureSelect(index)"
                >
                    <font-awesome-icon 
                        v-if="feature.isFontAwesome" 
                        class="footerImg"
                        :icon="feature.iconName"
                        size="lg"
                        alt=""
                    ></font-awesome-icon>
                    <img 
                        v-else :id="feature.featureType" 
                        class="feature-icon"
                        :src="feature.iconName" 
                        alt=""
                    />
                    <span>{{ feature.displayName }}</span>
                </button>
            </div>
        </div>
        <div id="applyWrapper">
            <button id="applyFeatures" class="btn-uofg-primary btn-full-width" @click="applySelection()">Apply</button>
        </div>

    </div>
</template>

<script setup>
import {ref, defineEmits, onMounted, onBeforeUnmount} from 'vue'
import axios from 'axios'
import { useRoute, useRouter } from 'vue-router'
import {fireAnalyticsSetup} from '@/composables/FirebaseAnalytics.js'

const {mapFeaturesListener} = fireAnalyticsSetup()
const route = useRoute()
const router = useRouter()
const emit = defineEmits(['featuresSelected', 'close'])

const features = ref([
  {
    displayName: 'Accessible Parking',
    featureType: 'AccessibleParking',
    queryString: 'showAccessibleParking',
    show: false,
    pressed: false,
    mapIcon: 'https://www.gla.ac.uk/t4/uofglife/app_assets/nearme/accessibleparking.png',
    iconName: 'car',
    isFontAwesome: true
  },
  {
    displayName: 'Accessible Toilets',
    featureType: 'AccessibleToilet',
    queryString: 'showAccessibleToilets',
    show: false,
    pressed: false,
    mapIcon: 'https://www.gla.ac.uk/t4/uofglife/app_assets/nearme/accessibletoilet.png',
    iconName: 'wheelchair',
    isFontAwesome: true
  },
  {
    displayName: 'ATMs',
    featureType: 'ATM',
    queryString: 'showAtms',
    show: false,
    pressed: false,
    mapIcon: 'https://www.gla.ac.uk/t4/uofglife/app_assets/nearme/atm.png',
    iconName: 'pound-sign',
    isFontAwesome: true
  },
  {
    displayName: 'Baby Changing',
    featureType: 'BabyChanging',
    queryString: 'showBabyChanging',
    show: false,
    pressed: false,
    mapIcon: 'https://www.gla.ac.uk/t4/uofglife/app_assets/nearme/babychanging.png',
    iconName: 'baby',
    isFontAwesome: true
  },
  {
    displayName: 'Bike Racks',
    featureType: 'BikeRack',
    queryString: 'showBikeRacks',
    show: false,
    pressed: false,
    mapIcon: 'https://www.gla.ac.uk/t4/uofglife/app_assets/nearme/bikerack.png',
    iconName: 'bicycle',
    isFontAwesome: true
  },
  {
    displayName: 'Defibrillators',
    featureType: 'Defibrillator',
    queryString: 'showDefibrillators',
    show: false,
    pressed: false,
    mapIcon: 'https://www.gla.ac.uk/t4/uofglife/app_assets/nearme/defibrillator.png',
    iconName: require('@/assets/defibrillator.svg'),
    isFontAwesome: false
  },
  {
    displayName: 'Food Outlets',
    featureType: 'FoodOutlet',
    queryString: 'showFoodOutlets',
    show: false,
    pressed: false,
    mapIcon: 'https://www.gla.ac.uk/t4/uofglife/app_assets/nearme/foodoutlet.png',
    iconName: 'coffee',
    isFontAwesome: true
  },
  {
    displayName: 'Period Products',
    featureType: 'PeriodProduct',
    queryString: 'showPeriodProducts',
    show: false,
    pressed: false,
    mapIcon: 'https://www.gla.ac.uk/t4/uofglife/app_assets/nearme/periodproduct.png',
    iconName: require('@/assets/period-productLG.svg'),
    isFontAwesome: false
  },
  {
    displayName: 'Prayer And Reflection',
    featureType: 'PrayerAndReflection',
    queryString: 'showPrayerAndReflection',
    show: false,
    pressed: false,
    mapIcon: 'https://www.gla.ac.uk/t4/uofglife/app_assets/nearme/prayerandreflection.png',
    iconName: require('@/assets/PrayerAndReflectionLG.svg'),
    isFontAwesome: false
  },
  {
    displayName: 'Retail Outlets',
    featureType: 'RetailOutlet',
    queryString: 'showRetailOutlets',
    show: false,
    pressed: false,
    mapIcon: 'https://www.gla.ac.uk/t4/uofglife/app_assets/nearme/retailoutlet.png',
    iconName: require('@/assets/retail-outletLG.svg'),
    isFontAwesome: false
  },
  {
    displayName: 'Student Support',
    featureType: 'StudentSupportOfficer',
    queryString: 'showStudentSupportOfficers',
    show: false,
    pressed: false,
    mapIcon: 'https://www.gla.ac.uk/t4/uofglife/app_assets/nearme/studentsupportofficer.png',
    iconName: require('@/assets/studentsupportofficers.svg'),
    isFontAwesome: false
  },
  {
    displayName: 'Showers',
    featureType: 'Shower',
    queryString: 'showShowers',
    show: false,
    pressed: false,
    mapIcon: 'https://www.gla.ac.uk/t4/uofglife/app_assets/nearme/shower.png',
    iconName: 'shower',
    isFontAwesome: true
  },
  {
    displayName: 'Toilets',
    featureType: 'Toilet',
    queryString: 'showToilets',
    show: false,
    pressed: false,
    mapIcon: 'https://www.gla.ac.uk/t4/uofglife/app_assets/nearme/toilet.png',
    iconName: 'restroom',
    isFontAwesome: true
  },
  {
    displayName: 'Attractions',
    featureType: 'Attraction',
    queryString: 'showAttractions',
    show: false,
    pressed: false,
    mapIcon: 'https://www.gla.ac.uk/t4/uofglife/app_assets/nearme/attraction.png',
    iconName: 'camera',
    isFontAwesome: true
  },
  {
    displayName: 'Water Fountains',
    featureType: 'WaterFountain',
    queryString: 'showWaterFountains',
    show: false,
    pressed: false,
    mapIcon: 'https://www.gla.ac.uk/t4/uofglife/app_assets/nearme/waterfountain.png',
    iconName: 'tint',
    isFontAwesome: true
  },
])
const selectedCount = ref(0)

onMounted(
  async () => {
    await addExternalRelationsFeatures()
    selectedFromUrl()
    //Add map feature selection analytics
    mapFeaturesListener('add')
  }
)

onBeforeUnmount(
  () => {
    //Remove event listener for map feature selection analytics or it will duplicate if user returns to this page and this component gets mounted again
    mapFeaturesListener('remove')
  }
)

/**
 * Toggles the show and pressed properties of the specified feature.
 * Updates any linked features e.g Toilets & Accessible toilets
 * Updates selected counter
 * 
 * @param {number} index - Array index of selected item in features array
 * @returns {void}
 */
async function featureSelect(index) {
    //Toggle show and pressed properties
    features.value[index].show = !features.value[index].show
    features.value[index].pressed = !features.value[index].pressed

    // when changing toilet state, match accessibleToiletState
    if (features.value[index].featureType === 'Toilet') {
        linkFeatureState(features.value[index], 'AccessibleToilet');
    }

    updateSelectedCount()

}

/**
 * Calls function to make API call that returns data of selected features including location
 * Calls function to create marker objects for selected features
 * Emits marker objects to parent component
 * 
 * @returns {void}
 */
async function applySelection() {

    //Get feature locations from API
    const displayFeatures = await getFeatures()
    //Create map marker objects
    const markerData = updateFeatureMarkers(displayFeatures)
    //Send map marker objects to map
    emit('featuresSelected', markerData)
    //Close drawer
    emit('close')

    updateFeatureUrl()
}

/**
 * Update the counter showing the number of features selected
 * 
 * @returns {void}
 */
function updateSelectedCount() {
    //Create array of features with show property set to true
    const selected = features.value.filter(
        feature => feature.show === true
    )
    //Number of features selected = length of array
    selectedCount.value = selected.length

}

/**
 * Loop through each feature and set the show and pressed properties to false
 * 
 * @returns {void}
 */
function clearSelections() {
    features.value.forEach(
        feature => {
            feature.show = false
            feature.pressed = false
        }
    )

    updateSelectedCount()
}


/**
 * Make call to API to get properties of features to show
 * @returns {array} - Array of feature objects
 */
async function getFeatures() {
    const featuresResponse = await axios.get(
    createFeatureRequest(),
    {withCredentials: false}
  )
  return featuresResponse.data.features
}

/**
 * Create URL to request feature locations from api
 * Loops through all the features, appends them to the URL with true(show) or false(dont show)
 * @returns {string} The request URL
 */
function createFeatureRequest() {
  let featureRequest = '/map/features?showBuildings=false&showAccessibleRoutes=false'

    features.value.forEach(
        feature => {
            featureRequest += '&' + feature.queryString + '=' + feature.show
        }
    )

    return featureRequest
}

/**
 * Creates array of marker objects for selected features
 *
 * @param {array} features - Feature objects from API
 * @returns {array} - Marker objects
 */
function updateFeatureMarkers(features) {

    const featuresMarkerData = features.map((feature) => {
        return {
        markerOptions: {
            position: {lat: feature.geometry.coordinates[1], lng: feature.geometry.coordinates[0]},
            content: createMapIcon(feature.properties.featureType)},
            name: feature.properties.featureName,
            description: feature.properties.featureInfo,
        };
    })

    return featuresMarkerData
}

/**
 * Creates map icon element for feature
 * TODO: Could a map-feature css class be applied instead individual properties?
 *
 * @param {string} featureType - Feature name
 * @returns {HTMLDivElement} - Feature marker
 */
function createMapIcon(featureType) {

    //Get feature object from features array
    const ftr = getFtr(featureType);
    //Create image wrapper
    const container = document.createElement('div');
    container.classList.add('feature-marker')
    //Create image element
    const img = document.createElement('img');
    img.src = ftr.mapIcon;
    img.alt = ftr.displayName;

    container.appendChild(img);
    return container;
}

/**
 * Returns feature object from features array given feature name
 *
 * @param {string} featureType - Feature name
 * @returns {object} - Feature
 */
function getFtr(featureType) {
  return features.value.find((ftr) => ftr.featureType === featureType);
}

/**
 * Updates a feature's pressed and show properties to match another feature
 *
 * @param {object} sourceFeature - The feature we want to match
 * @param {string} linkedFeatureType - The feature we want to update
 * @returns {void}
 */
function linkFeatureState(sourceFeature, linkedFeatureType) {
  //get index of feature name
  let linkedFeatureIndex = features.value.findIndex((element => element.featureType === linkedFeatureType))

  //Update linked feature state to match source feature
  features.value[linkedFeatureIndex].pressed = sourceFeature.pressed
  features.value[linkedFeatureIndex].show = sourceFeature.show
}

/**
 * Gets url query 'features'
 * Updates selected count
 * Loop through all features, if feature name in url query set to show
 *
 * @returns {void}
 */
function selectedFromUrl(){

  //Url example ?features=defibrillators-atms-bikeracks
  const selectedFromUrl = (route.query.features || '').split('-').map(s => s.toLowerCase())

  //Loop through all features. If feature display name is in the URL string set to show
  features.value.forEach((feature) => {
    if (selectedFromUrl.includes(feature.displayName.toLowerCase().replaceAll(' ', ''))) {
      feature.pressed = true
      feature.show = true
      selectedCount.value++
    }
  })

  applySelection()
}

/**
 * Create/update 'features' url query based on what is currently showing
 *
 * @returns {void}
 */
function updateFeatureUrl() {

  //Loop through features. Combine names of features formatted for url e.g "defibrillators-atms-bikeracks"
  let featuresQuery = ''
  features.value.forEach(
    (feature) => {
      if(feature.show) {
        featuresQuery += feature.displayName.toLowerCase().replaceAll(' ', '') + '-'
      }
    }
  )

  //Update features query if new values or remove if no features selected
  const query =  { ...route.query}

  if(featuresQuery!=='') {
    //Remove dash from end
    featuresQuery = featuresQuery.slice(0,-1)
    query.features = featuresQuery
  } else {
    if(query.features) {
      delete query.features
    }
  }

  router.replace({query})
}


/**
 * Get additional features (e.g. graduation, exam locations) from api
 * Insert into start of features array
 * 
 * @returns {void}
 */
async function addExternalRelationsFeatures() {
  await axios.get('/timetable/public/externalrelations/value')
    .then(async (response) => {
       await axios.get('/timetable/public/externalrelations/name')
        .then(response2 => {
          if (response.data) {
            //Get the features and their names into arrays from the response
            const additionalFeatures = response.data.split(",")
            const additionalFeaturesName = response2.data.split(",")
            //Loop through each feature and add its details to the existing features array
            additionalFeatures.forEach(
              (addFtr, index) => {
                features.value.unshift(
                  {
                    displayName: additionalFeaturesName[index],
                    featureType: addFtr,
                    queryString: "show" + addFtr,
                    show: false,
                    pressed: false,
                    mapIcon: 'https://www.gla.ac.uk/t4/uofglife/app_assets/nearme/' + addFtr.toLowerCase() + '.png',
                    iconName: require('@/assets/' + addFtr + '.svg'),
                    isFontAwesome: false
                  }
                )
              }
            )
          }
        })
    })
}
</script>

<style>
.featureButton {
    background: #fff;
    border: none;
    width:100%;
    padding: 0.3rem 0px;
    border-radius: 5px;
    align-items: center;
    color: #000;
    border: solid 1px #fff;
}

.featureButton svg,
.featureButton img{
    height: 25px;
}

.featureButton svg {
    color: var(--uog-dark-blue);
}

.featureButton.selected {
    background: var(--uog-blue-10);
}

.btn-uofg-primary {
    font-size: 20px;
    background-color: var(--uog-dark-blue);
    border: solid 1px var(--uog-dark-blue);
    padding: 9px 16px;
    color: #fff;
    margin: 4px 0px;
    line-height: normal;
    transition: background-color 0.4s, color 0.4s, border-color 0.4s;
    border-radius: 5px;
}

.btn-full-width {
    width:100%;
}

#featuresContainer #clearFeatures {
    background: transparent;
    border: none;
    text-decoration: underline;
    height:44px;
    min-width:44px;
    margin-left:10px;
    border-radius:5px;
    color: var(--uog-dark-blue);
    border: solid 1px #fff;
}

#selectedSummary p {
    margin-bottom:0px;
}

#featuresWrapper .feature-wrap {
    margin-top: 10px;
    padding: 0px 0.3rem;
}

#applyWrapper {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 100%;
    padding: 1rem;
    background-color: #fff;
    border-top: solid 1px #ccc;
}

#featuresContainer #clearFeatures:active,
#applyFeatures:active {
    background: var(--uog-blue-10);
}

@media(hover: hover) {
  .featureButton:hover {
    cursor:pointer;
    border: solid 1px #ccc;
  }

  #applyFeatures:hover {
    background-color: #fff;
    color: var(--uog-dark-blue);
  }

  #featuresContainer #clearFeatures:hover {
    text-decoration:none;
    border: solid 1px #ccc;
  }
}

</style>
